/**
 * Where the game gives way to the continuum on the mobile fallback.
 *
 * Taken from the design canvas source. Two layers do the work and they are deliberately
 * not the same shape: the media is masked out from the top down, and a wash of stage
 * colour closes in over what remains. Running both means the picture thins as the colour
 * thickens, which is what makes the join read as a dissolve rather than a crop line.
 *
 * The canvas notes are explicit that this is easy to get wrong: "frame for the visible
 * band; let opaque purple own the bottom", and do not reach for a full-height cover.
 */

import { toRgbChannels } from './fallbackColor';

/**
 * How much of the media survives, top to bottom.
 *
 * Full strength through the top quarter, then a long fall-off to nothing. The media element
 * itself is full-bleed; this is what shapes it, so there is no element edge to show.
 */
export const MEDIA_MASK_STOPS: ReadonlyArray<readonly [percent: number, alpha: number]> = [
  [0, 1],
  [24, 1],
  [36, 0.9],
  [48, 0.68],
  [58, 0.42],
  [68, 0.2],
  [78, 0.06],
  [88, 0],
  [100, 0],
];

/**
 * Percent down the screen, and how much of the continuum has closed over the game by then.
 *
 * Starts at nothing and stays there through the top quarter. The campaign owns the top
 * of the phone; the continuum only starts closing once there is copy to carry. It must
 * be fully opaque by the foot of the media band so leftover letterbox cannot read as a
 * black strip in the fade.
 */
export const WASH_STOPS: ReadonlyArray<readonly [percent: number, alpha: number]> = [
  [0, 0],
  [26, 0],
  [38, 0.12],
  [50, 0.32],
  [58, 0.55],
  [66, 0.82],
  [74, 1],
  [100, 1],
];

export type StageWashStops = ReadonlyArray<readonly [percent: number, alpha: number]>;

export const buildMediaMask = (stops: StageWashStops = MEDIA_MASK_STOPS): string => {
  const parts = stops.map(([percent, alpha]) => `rgba(0, 0, 0, ${alpha}) ${percent}%`);

  return `linear-gradient(to bottom, ${parts.join(', ')})`;
};

export const buildContinuumWash = (
  stageColor: string,
  stops: StageWashStops = WASH_STOPS,
): string => {
  const channels = toRgbChannels(stageColor);
  // A colour we cannot take apart cannot be faded stop by stop. Two stops still dissolve
  // rather than cut, which is the point.
  if (!channels) return `linear-gradient(to bottom, transparent 0%, ${stageColor} 90%)`;

  const parts = stops.map(([percent, alpha]) => `rgba(${channels}, ${alpha}) ${percent}%`);
  return `linear-gradient(to bottom, ${parts.join(', ')})`;
};
